import type { ObjectWorkspaceWorkbenchContext } from '@heidi/agents';
import { closeArtifactAtom, openArtifactAtom } from '@heidi/agents/data';
import { useStore } from 'jotai';
import {
  useCallback,
  useEffect,
  useRef,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useIntl } from 'react-intl';

import { objectWorkspaceChatHarnessMessages as messages } from './object-workspace-chat-harness.messages';
import {
  emptyObjectWorkspaceChatTab,
  isObjectWorkspaceChatTabProtected,
  normalizedObjectWorkspaceTabTitle,
  type ObjectWorkspaceChatAnnounce,
  type ObjectWorkspaceChatHarnessState,
} from './object-workspace-chat-utils';
import type { ObjectWorkspaceChatTab } from './object-workspace-project-chat-tab';

type TaskSelection = NonNullable<ObjectWorkspaceWorkbenchContext['taskSelection']>;
type ProjectSelection = NonNullable<ObjectWorkspaceWorkbenchContext['projectSelection']>;

interface UseObjectWorkspacePromptTabsArgs {
  object: ObjectWorkspaceWorkbenchContext['object'];
  anchorSelection: ObjectWorkspaceWorkbenchContext['anchorSelection'];
  taskSelection: ObjectWorkspaceWorkbenchContext['taskSelection'];
  projectSelection: ObjectWorkspaceWorkbenchContext['projectSelection'];
  clearTaskSelection: ObjectWorkspaceWorkbenchContext['clearTaskSelection'];
  newTabTitle: string;
  announce: ObjectWorkspaceChatAnnounce;
  setState: Dispatch<SetStateAction<ObjectWorkspaceChatHarnessState>>;
}

export function useObjectWorkspacePromptTabs({
  object,
  anchorSelection,
  taskSelection,
  projectSelection,
  clearTaskSelection,
  newTabTitle,
  announce,
  setState,
}: UseObjectWorkspacePromptTabsArgs): {
  addEmptyTab: () => void;
  takeTaskLaunches: (tabId: string) => void;
} {
  const intl = useIntl();
  const store = useStore();
  const nextTabNumber = useRef(1);
  const handledAnchorSequence = useRef<number>(undefined);
  const handledTaskSequence = useRef<number>(undefined);
  const handledProjectSequence = useRef<number>(undefined);

  const addEmptyTab = useCallback(() => {
    const number = ++nextTabNumber.current;
    const id = `${object.kind}-${object.id}-work-${number}`;
    setState((current) =>
      announce(
        current,
        {
          activeTabId: id,
          tabs: [...current.tabs, emptyObjectWorkspaceChatTab(id, newTabTitle)],
        },
        intl.formatMessage(messages.emptyTabAdded, { number })
      )
    );
  }, [announce, intl, newTabTitle, object.id, object.kind, setState]);

  const startPrompt = useCallback(
    (
      prompt: string,
      source: { anchorKey?: string; sessionId?: string; taskSelection?: TaskSelection }
    ) => {
      const newTabId = `${object.kind}-${object.id}-work-${++nextTabNumber.current}`;
      setState((current) => {
        const launches = source.taskSelection?.launches;
        // A send still holds its launches until the create settles.
        const existing = current.tabs.find(
          (tab) =>
            !tab.isOpeningTurnInFlight &&
            ((source.anchorKey && tab.anchorKey === source.anchorKey) ||
              (launches && tab.taskLaunches && !tab.projectId))
        );
        if (launches?.length === 0) {
          if (!existing) return current;
          return {
            ...current,
            tabs: current.tabs.map((tab) =>
              tab.id === existing.id
                ? { ...tab, prefilledPrompt: undefined, taskLaunches: undefined }
                : tab
            ),
          };
        }
        if (existing && !launches) {
          return announce(
            current,
            { ...current, activeTabId: existing.id },
            intl.formatMessage(messages.tabSelected, { title: existing.title })
          );
        }

        const active = current.tabs.find((tab) => tab.id === current.activeTabId);
        const canReuse =
          active &&
          active.kind !== 'heidi' &&
          !active.projectId &&
          !isObjectWorkspaceChatTabProtected(active);
        const base = existing ?? (canReuse ? active : undefined);
        const targetId = base?.id ?? newTabId;
        const target: ObjectWorkspaceChatTab = {
          ...(base ?? emptyObjectWorkspaceChatTab(targetId, newTabTitle)),
          id: targetId,
          kind: 'work',
          title: normalizedObjectWorkspaceTabTitle(prompt, newTabTitle),
          prefilledPrompt: prompt,
          isDraftEmpty: false,
          taskLaunches: launches,
          anchorKey: source.anchorKey,
          sessionId: source.sessionId,
        };
        return announce(
          current,
          {
            activeTabId: targetId,
            tabs: base
              ? current.tabs.map((tab) => (tab.id === targetId ? target : tab))
              : [...current.tabs, target],
          },
          intl.formatMessage(base ? messages.tabSelected : messages.tabAdded, {
            title: target.title,
          })
        );
      });
    },
    [announce, intl, newTabTitle, object.id, object.kind, setState]
  );

  const openLinkedProject = useCallback(
    (selection: ProjectSelection) => {
      const title = normalizedObjectWorkspaceTabTitle(selection.title, newTabTitle);
      setState((current) => {
        const existing = current.tabs.find(
          (tab) => tab.projectId === selection.projectId
        );
        if (existing) {
          return announce(
            current,
            { ...current, activeTabId: existing.id },
            intl.formatMessage(messages.tabSelected, { title: existing.title })
          );
        }
        const id = `${object.kind}-${object.id}-work-${++nextTabNumber.current}`;
        const target: ObjectWorkspaceChatTab = {
          ...emptyObjectWorkspaceChatTab(id, title),
          projectId: selection.projectId,
        };
        return announce(
          current,
          {
            activeTabId: id,
            tabs: [...current.tabs, target],
          },
          intl.formatMessage(messages.tabAdded, { title: target.title })
        );
      });
    },
    [announce, intl, newTabTitle, object.id, object.kind, setState]
  );

  useEffect(() => {
    if (!taskSelection || handledTaskSequence.current === taskSelection.sequence) return;
    handledTaskSequence.current = taskSelection.sequence;
    startPrompt(taskSelection.prompt, { taskSelection });
  }, [startPrompt, taskSelection]);
  useEffect(() => {
    if (!anchorSelection || handledAnchorSequence.current === anchorSelection.sequence)
      return;
    handledAnchorSequence.current = anchorSelection.sequence;
    const { anchor } = anchorSelection;
    if (anchor.kind === 'session') {
      // The canvas grounds the turn and is carried into the new chat, so it must not name another subject.
      const open = store.get(openArtifactAtom);
      if (open && !('sessionId' in open && open.sessionId === anchor.id)) {
        store.set(closeArtifactAtom);
      }
    }
    startPrompt(anchorSelection.prompt, {
      anchorKey: `${anchor.kind}:${anchor.id}`,
      sessionId: anchor.kind === 'session' ? anchor.id : undefined,
    });
  }, [anchorSelection, startPrompt, store]);
  useEffect(() => {
    if (
      !projectSelection ||
      handledProjectSequence.current === projectSelection.sequence
    ) {
      return;
    }
    handledProjectSequence.current = projectSelection.sequence;
    openLinkedProject(projectSelection);
  }, [openLinkedProject, projectSelection]);

  const takeTaskLaunches = useCallback(
    (tabId: string) => {
      setState((current) => ({
        ...current,
        tabs: current.tabs.map((tab) =>
          tab.id === tabId ? { ...tab, taskLaunches: undefined } : tab
        ),
      }));
      clearTaskSelection();
    },
    [clearTaskSelection, setState]
  );

  return { addEmptyTab, takeTaskLaunches };
}
